import React, {useState, useEffect} from "react";
import {
    Card,
    CardBody,
    CardHeader,
    Spinner, Typography,
} from "@material-tailwind/react";
import {StatisticsCard} from "../../widgets/cards";
import {StatisticsChart} from "../../widgets/charts";
import {ArrowTrendingDownIcon, DocumentTextIcon, CurrencyDollarIcon} from "@heroicons/react/24/solid";
import "react-datepicker/dist/react-datepicker.css";
import axios from 'axios';
import {NumericFormat} from "react-number-format";

const Dashboard = ({username, roleScope}) => {
    const today = formatDate(new Date());

    const [invoices, setInvoices] = useState([]);
    const [totalAmount, setTotalAmount] = useState(0);
    const [totalAccounts, setTotalAccounts] = useState(0);
    const [totalCancellations, setTotalCancellations] = useState(0);
    const [totalsByType, setTotalsByType] = useState(null);
    const [chartUserData, setChartUserData] = useState(null);
    const [chartDateData, setChartDateData] = useState(null);
    const [chartTotalsData, setChartTotalsData] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [displayTitle, setDisplayTitle] = useState(today);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingTotal, setLoadingTotal] = useState(false);
    const [loadingTotals, setLoadingTotals] = useState(false);
    const [loadingTotalsUser, setLoadingTotalsUser] = useState(false);
    const [loadingTotalsDay, setLoadingTotalsDay] = useState(false);
    const [loadingTotalOnline, setLoadingTotalOnline] = useState(false);
    const [totalAmountOnline, setTotalAmountOnline] = useState(0);
    const [totalAccountsOnline, setTotalAccountsOnline] = useState(0);
    const [chartDateOnlineData, setChartDateOnlineData] = useState(null);
    const [chartTotalsOnlineData, setChartTotalsOnlineData] = useState(null);
    const [loadingTotalsOnlineDay, setLoadingTotalsOnlineDay] = useState(false);

    useEffect(() => {
        fetchDashboardData(startDate, endDate);

        const intervalId = setInterval(() => {
            fetchDashboardData(startDate, endDate);
        }, 180000);

        return () => clearInterval(intervalId);
    }, []);

    const fetchDashboardData = (start_date, end_date) => {
        setIsLoading(true);

        fetchTotals(startDate, endDate);
        fetchTotalsByType(startDate, endDate);

        if (roleScope === 'admin' || roleScope === 'total') {
            fetchTotalsByUser(startDate, endDate);
            fetchTotalsPerDay(startDate, endDate);
            fetchTotalsOnline(startDate, endDate);
            fetchTotalsOnlinePerDay(startDate, endDate);
        } else {
            fetchInvoices();
        }
        setIsLoading(false);
    };

    const fetchTotals = async (start_date, end_date) => {
        setLoadingTotal(true);

        axios.get('/dashboard/total.json', {
            params: {
                start_date: start_date || '',
                end_date: end_date || ''
            }
        })
            .then(response => {
                const data = response.data;
                setTotalAmount(data.total);
                setTotalAccounts(data.accounts);
                setTotalCancellations(data.cancellations)
            })
            .catch(error => {
                console.error('Error fetching total: ', error);
            })
            .finally(() => {
                setLoadingTotal(false);
            });
    }

    const fetchTotalsByType = async (start_date, end_date) => {
        setLoadingTotals(true);

        axios.get('/dashboard/totals_by_type.json', {
            params: {
                start_date: start_date || '',
                end_date: end_date || ''
            }
        })
            .then(response => {
                const data = response.data;
                setTotalsByType(data);
            })
            .catch(error => {
                console.error('Error fetching total: ', error);
            })
            .finally(() => {
                setLoadingTotals(false);
            });
    }

    const fetchTotalsByUser = async (start_date, end_date) => {
        setLoadingTotalsUser(true);

        axios.get('/dashboard/totals_by_user.json', {
            params: {
                start_date: start_date || '',
                end_date: end_date || ''
            }
        })
            .then(response => {
                const data = response.data;
                setChartUserData(data);
            })
            .catch(error => {
                console.error('Error fetching total: ', error);
            })
            .finally(() => {
                setLoadingTotalsUser(false);
            });
    }

    const fetchTotalsPerDay = async (start_date, end_date) => {
        setLoadingTotalsDay(true);

        axios.get('/dashboard/totals_per_day.json', {
            params: {
                start_date: start_date || '',
                end_date: end_date || ''
            }
        })
            .then(response => {
                const {total_per_day, amount_per_day} = response.data;
                setChartDateData(total_per_day);
                setChartTotalsData(amount_per_day);
            })
            .catch(error => {
                console.error('Error fetching totals per day data: ', error);
            })
            .finally(() => {
                setLoadingTotalsDay(false);
            });
    }

    const fetchTotalsOnline = async (start_date, end_date) => {
        setLoadingTotalOnline(true);

        axios.get('/dashboard/total_online.json', {
            params: {
                start_date: start_date || '',
                end_date: end_date || ''
            }
        })
            .then(response => {
                const data = response.data;
                setTotalAmountOnline(data.total);
                setTotalAccountsOnline(data.accounts);
            })
            .catch(error => {
                console.error('Error fetching total online: ', error);
            })
            .finally(() => {
                setLoadingTotalOnline(false);
            });
    }

    const fetchTotalsOnlinePerDay = async (start_date, end_date) => {
        setLoadingTotalsOnlineDay(true);

        axios.get('/dashboard/totals_online_per_day.json', {
            params: {
                start_date: start_date || '',
                end_date: end_date || ''
            }
        })
            .then(response => {
                const {total_per_day, amount_per_day} = response.data;
                setChartDateOnlineData(total_per_day);
                setChartTotalsOnlineData(amount_per_day);
            })
            .catch(error => {
                console.error('Error fetching totals per day data: ', error);
            })
            .finally(() => {
                setLoadingTotalsOnlineDay(false);
            });
    }

    const fetchInvoices = async () => {
        axios.get('/dashboard/invoices.json')
            .then(response => {
                const data = response.data;
                setInvoices(data);
            })
            .catch(error => {
                console.error('Error fetching invoices data: ', error);
            });
    }

    const handleDateChange = () => {
        const formattedStartDate = formatDate(startDate);
        const formattedEndDate = formatDate(endDate);
        const title = startDate || endDate
            ? `${formattedStartDate ? formattedStartDate : '...'} a ${formattedEndDate ? formattedEndDate : '...'}`
            : `${today}`;

        setDisplayTitle(title);

        fetchDashboardData(startDate, endDate);
    };

    const getChartConfig = (data, name, key, value_type) => {
        if (!data || data.length === 0) return null;

        const categories = data.map(item => item[key]);
        const chartData = data.map(item => Math.ceil(item.total) || Math.ceil(item.count));

        const totalRecords = data.length;
        const defaultStartIndex = Math.max(0, totalRecords - 8);
        const defaultEndIndex = totalRecords;

        const visibleRangeData = chartData.slice(Math.max(0, totalRecords - 60), defaultEndIndex + 1);
        const yaxisMin = Math.min(...visibleRangeData) * 0.5;
        const yaxisMax = Math.max(...visibleRangeData) * 1.1;

        const xaxisMin = defaultStartIndex;
        const xaxisMax = defaultEndIndex;

        if (key === 'username') {
            return {
                type: "bar",
                height: 250,
                series: [
                    {
                        name: '',
                        data: chartData,
                    },
                ],
                options: {
                    colors: ["#388e3c"],
                    plotOptions: {
                        bar: {
                            columnWidth: "20%",
                            borderRadius: 5,
                        },
                    },
                    dataLabels: {
                        enabled: true,
                        formatter: function (val) {
                            return formatMoney(val)
                        },
                        offsetY: 0,
                        style: {
                            colors: ['#000']
                        }

                    },
                    xaxis: {
                        categories: categories.map(category =>
                            category ? category.toUpperCase() : category
                        ),
                    },
                    yaxis: {
                        labels: {
                            formatter: function (val) {
                                return formatMoney(val);
                            }
                        }
                    },
                },
            };
        } else {
            return {
                type: "line",
                height: 250,
                series: [
                    {
                        name: '',
                        data: chartData,
                    },
                ],
                options: {
                    colors: ["#0288d1"],
                    stroke: {
                        lineCap: "round",
                    },
                    markers: {
                        size: 5,
                    },
                    xaxis: {
                        min: xaxisMin,
                        max: xaxisMax,
                        categories: categories.map(category =>
                            category ? category.toUpperCase() : category
                        ),
                    },
                    yaxis: {
                        min: yaxisMin,
                        max: yaxisMax,
                        labels: {
                            formatter: function (val) {
                                if (value_type === 'integer') {
                                    return val;
                                } else {
                                    return formatMoney(val);
                                }
                            }
                        }
                    },
                },
            };
        }
    };


    // Generate chart configurations
    const userChartConfig = getChartConfig(chartUserData, "TOTAL RECAUDACIÓN POR CAJA", 'username', 'float');
    const dateChartConfig = getChartConfig(chartDateData, "TOTAL RECAUDACIÓN POR DIA", 'date', 'float');
    const totalChartConfig = getChartConfig(chartTotalsData, "TOTAL DE PREDIOS PAGADOS", 'date', 'integer');
    // Charts for Online transactions
    const dateOnlineChartConfig = getChartConfig(chartDateOnlineData, "TOTAL RECAUDACIÓN POR DIA EN LÍNEA", 'date', 'float');
    const totalOnlineChartConfig = getChartConfig(chartTotalsOnlineData, "TOTAL DE PREDIOS PAGADOS EN LÍNEA", 'date', 'integer');

    return (
        <>
            <div className="mt-12">
                {isLoading && (
                    <div className='absolute top-95-px right-4'>
                        <Spinner color="blue"/>
                    </div>
                )
                }
                <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
                    <div>
                        <h1 className="text-2xl font-bold">¡Hola {username.toUpperCase()}!</h1>
                        <h5 className="text-md text-blue-gray-900">{displayTitle}</h5>
                    </div>
                    {/*<div className="flex items-center gap-2">
                        {(startDate || endDate) &&
                            <button
                                onClick={() => {
                                    setStartDate("");
                                    setEndDate("");
                                    setDisplayTitle(today);
                                    fetchDashboardData('', '');
                                }}
                                className="text-xl p-2 bg-gray-200 rounded-full h-auto"
                                aria-label="Reset"
                            >
                                ✕
                            </button>
                        }
                        <DatePicker
                            selected={startDate}
                            onChange={(date) => setStartDate(date)}
                            className="border rounded px-4 py-2"
                            locale={es}
                            dateFormat="dd/MM/yyyy"
                            placeholderText="Fecha de inicio"
                        />
                        <span>a</span>
                        <DatePicker
                            selected={endDate}
                            onChange={(date) => setEndDate(date)}
                            className="border rounded px-4 py-2"
                            locale={es}
                            dateFormat="dd/MM/yyyy"
                            placeholderText="Fecha de fin"
                        />
                        <button
                            onClick={handleDateChange}
                            className="ml-2 bg-black text-white px-4 py-2 rounded"
                        >
                            Filtrar
                        </button>
                    </div>*/}
                </div>

                <div className="mb-12 grid gap-y-10 gap-x-6 md:grid-cols-2 xl:grid-cols-4">
                    {loadingTotals ? (
                        Array.from({length: 4}, (_, index) => (
                            <Card className="border border-blue-gray-100 shadow-sm animate-pulse"
                                  key={index}>
                                <CardHeader
                                    variant="gradient"
                                    color='gray'
                                    floated={false}
                                    shadow={true}
                                    className="absolute grid h-12 w-12 place-items-center"
                                >
                                    <CurrencyDollarIcon className="w-6 h-6 text-white"/>
                                </CardHeader>
                                <CardBody className="p-4 text-right">
                                    <Typography variant="small" className="font-normal text-blue-gray-600">
                                        &nbsp;
                                    </Typography>
                                    <Typography variant="h4" color="blue-gray">
                                        &nbsp;
                                    </Typography>
                                </CardBody>
                            </Card>
                        ))
                    ) : (totalsByType && totalsByType.map(({description, total}) => (
                        <StatisticsCard
                            key={description}
                            title={description}
                            value={<NumericFormat
                                value={total.toFixed(2)}
                                displayType={'text'}
                                prefix={'$'}
                                allowLeadingZeros
                                thousandSeparator=","
                            />}
                            color={'gray'}
                            icon={
                                <CurrencyDollarIcon className="w-6 h-6 text-white"/>
                            }
                        />
                    )))}
                </div>

                {roleScope === 'admin' || roleScope === 'total' ? (
                    <>
                        <div className="mb-6 grid grid-cols-1 gap-y-12 gap-x-6 md:grid-cols-1 xl:grid-cols-1">
                            {userChartConfig ? (
                                <StatisticsChart
                                    title="TOTAL RECAUDACIÓN POR USUARIO"
                                    chart={userChartConfig}

                                />
                            ) : (
                                <div
                                    className="flex justify-center items-center text-gray-500 border-2 rounded-md py-4">
                                    No hay registros en la fecha actual.
                                </div>
                            )}
                        </div>

                        <div className="mb-6 grid grid-cols-1 gap-y-12 gap-x-6 md:grid-cols-2 xl:grid-cols-2">
                            {dateChartConfig ? (
                                <StatisticsChart
                                    title="TOTAL RECAUDACIÓN POR DÍA"
                                    chart={dateChartConfig}
                                />
                            ) : (
                                <div
                                    className="flex justify-center items-center text-gray-500 border-2 rounded-md py-4">
                                    No hay registros en la fecha actual.
                                </div>
                            )}

                            {totalChartConfig ? (
                                <StatisticsChart
                                    title="TOTAL DE PREDIOS PAGADOS"
                                    chart={totalChartConfig}
                                />
                            ) : (
                                <div
                                    className="flex justify-center items-center text-gray-500 border-2 rounded-md py-4">
                                    No hay registros en la fecha actual.
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    <div className="mb-6 grid grid-cols-1 gap-6 xl:grid-cols-1">
                        <Card className="overflow-y-auto xl:col-span-2 border border-blue-gray-100 shadow-sm">
                            <CardHeader
                                floated={false}
                                shadow={false}
                                color="transparent"
                                className="m-0 flex items-center justify-between p-6"
                            >
                                <div>
                                    <Typography variant="h6" color="blue-gray" className="mb-1">
                                        Cuentas cobradas
                                    </Typography>
                                </div>
                            </CardHeader>
                            <CardBody className="overflow-x-scroll px-0 pt-0 pb-2">
                                <table className="w-full min-w-[640px] table-auto">
                                    <thead>
                                    <tr>
                                        {["Folio", "Clave", "Forma de pago", "Total", "Fecha"].map(
                                            (el) => (
                                                <th
                                                    key={el}
                                                    className="border-b border-blue-gray-50 py-3 px-6 text-left"
                                                >
                                                    <Typography
                                                        variant="small"
                                                        className="text-[11px] font-medium uppercase text-blue-gray-400"
                                                    >
                                                        {el}
                                                    </Typography>
                                                </th>
                                            )
                                        )}
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {invoices ? (
                                        invoices.map((invoice, key) => {
                                            const className = `py-3 px-5 ${
                                                key === invoices.length - 1 ? "" : "border-b border-blue-gray-50"
                                            }`;

                                            return (
                                                <tr key={invoice.id}>
                                                    <td className={className}>
                                                        <Typography variant="small" color="blue-gray"
                                                                    className="font-bold">
                                                            {invoice.id}
                                                        </Typography>
                                                    </td>
                                                    <td className={className}>
                                                        <Typography variant="small" color="blue-gray"
                                                                    className="font-bold">
                                                            {invoice.parcel_code}
                                                        </Typography>
                                                    </td>
                                                    <td className={className}>
                                                        <Typography variant="small"
                                                                    className="text-xs font-medium text-blue-gray-600">
                                                            {invoice.payment_type_name}
                                                        </Typography>
                                                    </td>
                                                    <td className={className}>
                                                        <Typography variant="small"
                                                                    className="text-xs font-medium text-blue-gray-600">
                                                            <NumericFormat
                                                                value={invoice.total.toFixed(2)}
                                                                displayType={'text'}
                                                                prefix={'$'}
                                                                allowLeadingZeros
                                                                thousandSeparator=","
                                                            />
                                                        </Typography>
                                                    </td>
                                                    <td className={className}>
                                                        <Typography variant="small"
                                                                    className="text-xs font-medium text-blue-gray-600">
                                                            {invoice.date}
                                                        </Typography>
                                                    </td>
                                                </tr>
                                            )
                                        })) : (
                                        <tr>
                                            <td colSpan='5' className='text-center'>
                                                <div
                                                    className="flex justify-center items-center text-gray-500 rounded-md py-4">
                                                    No hay registros en la fecha actual.
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                    </tbody>
                                </table>
                            </CardBody>
                        </Card>
                    </div>
                )}

                <div
                    className={`grid gap-y-10 gap-x-6 ${totalCancellations ? 'md:grid-cols-3 xl:grid-cols-4' : 'md:grid-cols-2 xl:grid-cols-4'}`}>
                    {loadingTotal ? (
                        <>
                            <Card
                                className="border border-blue-gray-100 shadow-sm animate-pulse bg-gray-300 xl:col-start-3 md:col-start-1">
                                <CardHeader
                                    variant="gradient"
                                    color='gray'
                                    floated={false}
                                    shadow={true}
                                    className="absolute grid h-12 w-12 place-items-center"
                                >
                                    <DocumentTextIcon className="w-6 h-6 text-white"/>
                                </CardHeader>
                                <CardBody className="p-4 text-right">
                                    <Typography variant="small" className="font-normal text-blue-gray-600">
                                        &nbsp;
                                    </Typography>
                                    <Typography variant="h4" color="blue-gray">
                                        &nbsp;
                                    </Typography>
                                </CardBody>
                            </Card>
                            <Card
                                className="border border-blue-gray-100 shadow-sm animate-pulse bg-gray-300 xl:col-start-4 md:col-start-2">
                                <CardHeader
                                    variant="gradient"
                                    color='gray'
                                    floated={false}
                                    shadow={true}
                                    className="absolute grid h-12 w-12 place-items-center"
                                >
                                    <CurrencyDollarIcon className="w-6 h-6 text-white"/>
                                </CardHeader>
                                <CardBody className="p-4 text-right">
                                    <Typography variant="small" className="font-normal text-blue-gray-600">
                                        &nbsp;
                                    </Typography>
                                    <Typography variant="h4" color="blue-gray">
                                        &nbsp;
                                    </Typography>
                                </CardBody>
                            </Card>
                        </>
                    ) : totalAmount && (
                        <>
                            <Card
                                className={`border border-blue-gray-100 shadow-sm md:col-start-1 ${totalCancellations ? 'xl:col-start-2' : 'xl:col-start-3'}`}>
                                <CardHeader
                                    variant="gradient"
                                    color='gray'
                                    floated={false}
                                    shadow={true}
                                    className="absolute grid h-12 w-12 place-items-center"
                                >
                                    <DocumentTextIcon className="w-6 h-6 text-white"/>
                                </CardHeader>
                                <CardBody className="p-4 text-right">
                                    <Typography variant="small" className="font-normal text-blue-gray-600">
                                        CUENTAS
                                    </Typography>
                                    <Typography variant="h4" color="blue-gray">
                                        {totalAccounts}
                                    </Typography>
                                </CardBody>
                            </Card>
                            {totalCancellations && (
                                <Card
                                    className="border border-blue-gray-100 shadow-sm xl:col-start-3 md:col-start-2">
                                    <CardHeader
                                        variant="gradient"
                                        color='gray'
                                        floated={false}
                                        shadow={true}
                                        className="absolute grid h-12 w-12 place-items-center"
                                    >
                                        <ArrowTrendingDownIcon className="w-6 h-6 text-white"/>
                                    </CardHeader>
                                    <CardBody className="p-4 text-right">
                                        <Typography variant="small" className="font-normal text-blue-gray-600">
                                            CANCELACIONES
                                        </Typography>
                                        <Typography variant="h4" color="blue-gray">
                                            <NumericFormat
                                                value={`-${totalCancellations.toFixed(2)}`}
                                                displayType={'text'}
                                                prefix={'$'}
                                                allowLeadingZeros
                                                thousandSeparator=","
                                            />
                                        </Typography>
                                    </CardBody>
                                </Card>
                            )}
                            <Card
                                className={`border border-blue-gray-100 shadow-sm xl:col-start-4 ${totalCancellations ? 'md:col-start-3' : 'md:col-start-2'}`}>
                                <CardHeader
                                    variant="gradient"
                                    color='gray'
                                    floated={false}
                                    shadow={true}
                                    className="absolute grid h-12 w-12 place-items-center"
                                >
                                    <CurrencyDollarIcon className="w-6 h-6 text-white"/>
                                </CardHeader>
                                <CardBody className="p-4 text-right">
                                    <Typography variant="small" className="font-normal text-blue-gray-600">
                                        TOTAL
                                    </Typography>
                                    <Typography variant="h4" color="blue-gray">
                                        <NumericFormat
                                            value={totalAmount.toFixed(2) - (totalCancellations ? totalCancellations.toFixed(2) : 0)}
                                            displayType={'text'}
                                            prefix={'$'}
                                            allowLeadingZeros
                                            thousandSeparator=","
                                        />
                                    </Typography>
                                </CardBody>
                            </Card>
                        </>
                    )}
                </div>

                {(roleScope === 'admin' || roleScope === 'total') && (
                    <>
                        <hr className='my-6'/>
                        <div
                            className={`flex flex-col`}>
                            <h6 className='mb-3 block antialiased tracking-normal font-sans text-base font-semibold leading-relaxed text-blue-gray-900'>
                                TRANSACCIONES EN LÍNEA
                            </h6>
                            <div className="mb-6 grid grid-cols-1 gap-y-12 gap-x-6 md:grid-cols-2 xl:grid-cols-2">
                                {dateOnlineChartConfig ? (
                                    <StatisticsChart
                                        title="TOTAL RECAUDACIÓN POR DÍA"
                                        chart={dateOnlineChartConfig}
                                    />
                                ) : (
                                    <div
                                        className="flex justify-center items-center text-gray-500 border-2 rounded-md py-4">
                                        No hay registros en la fecha actual.
                                    </div>
                                )}

                                {totalOnlineChartConfig ? (
                                    <StatisticsChart
                                        title="TOTAL DE PREDIOS PAGADOS"
                                        chart={totalOnlineChartConfig}
                                    />
                                ) : (
                                    <div
                                        className="flex justify-center items-center text-gray-500 border-2 rounded-md py-4">
                                        No hay registros en la fecha actual.
                                    </div>
                                )}
                            </div>
                        </div>
                        <div
                            className={`mb-12 grid gap-y-10 gap-x-6 md:grid-cols-2 xl:grid-cols-4`}>
                            {loadingTotalOnline ? (
                                <>
                                    <Card
                                        className="border border-blue-gray-100 shadow-sm animate-pulse bg-gray-300 xl:col-start-3 md:col-start-1">
                                        <CardHeader
                                            variant="gradient"
                                            color='gray'
                                            floated={false}
                                            shadow={true}
                                            className="absolute grid h-12 w-12 place-items-center"
                                        >
                                            <DocumentTextIcon className="w-6 h-6 text-white"/>
                                        </CardHeader>
                                        <CardBody className="p-4 text-right">
                                            <Typography variant="small" className="font-normal text-blue-gray-600">
                                                &nbsp;
                                            </Typography>
                                            <Typography variant="h4" color="blue-gray">
                                                &nbsp;
                                            </Typography>
                                        </CardBody>
                                    </Card>
                                    <Card
                                        className="border border-blue-gray-100 shadow-sm animate-pulse bg-gray-300 xl:col-start-4 md:col-start-2">
                                        <CardHeader
                                            variant="gradient"
                                            color='gray'
                                            floated={false}
                                            shadow={true}
                                            className="absolute grid h-12 w-12 place-items-center"
                                        >
                                            <CurrencyDollarIcon className="w-6 h-6 text-white"/>
                                        </CardHeader>
                                        <CardBody className="p-4 text-right">
                                            <Typography variant="small" className="font-normal text-blue-gray-600">
                                                &nbsp;
                                            </Typography>
                                            <Typography variant="h4" color="blue-gray">
                                                &nbsp;
                                            </Typography>
                                        </CardBody>
                                    </Card>
                                </>
                            ) : totalAmountOnline && (
                                <>
                                    <Card
                                        className={`border border-blue-gray-100 shadow-sm md:col-start-1 xl:col-start-3`}>
                                        <CardHeader
                                            variant="gradient"
                                            color='gray'
                                            floated={false}
                                            shadow={true}
                                            className="absolute grid h-12 w-12 place-items-center"
                                        >
                                            <DocumentTextIcon className="w-6 h-6 text-white"/>
                                        </CardHeader>
                                        <CardBody className="p-4 text-right">
                                            <Typography variant="small" className="font-normal text-blue-gray-600">
                                                CUENTAS
                                            </Typography>
                                            <Typography variant="h4" color="blue-gray">
                                                {totalAccountsOnline}
                                            </Typography>
                                        </CardBody>
                                    </Card>
                                    <Card
                                        className={`border border-blue-gray-100 shadow-sm xl:col-start-4 md:col-start-2`}>
                                        <CardHeader
                                            variant="gradient"
                                            color='gray'
                                            floated={false}
                                            shadow={true}
                                            className="absolute grid h-12 w-12 place-items-center"
                                        >
                                            <CurrencyDollarIcon className="w-6 h-6 text-white"/>
                                        </CardHeader>
                                        <CardBody className="p-4 text-right">
                                            <Typography variant="small" className="font-normal text-blue-gray-600">
                                                TOTAL
                                            </Typography>
                                            <Typography variant="h4" color="blue-gray">
                                                <NumericFormat
                                                    value={totalAmountOnline.toFixed(2)}
                                                    displayType={'text'}
                                                    prefix={'$'}
                                                    allowLeadingZeros
                                                    thousandSeparator=","
                                                />
                                            </Typography>
                                        </CardBody>
                                    </Card>
                                </>
                            )}
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

const formatDate = (dateString) => {
    if (!dateString) return "";

    // Check if it's already a Date object, and if not, parse it.
    const date = new Date(dateString);

    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are 0-based, so add 1
    const day = date.getDate().toString().padStart(2, '0'); // Ensure the day is two digits

    return `${day}/${month}/${year}`;
};
const formatMoney = (amount) => {
    if (!amount) return "$0";
    const roundedAmount = Math.ceil(amount);
    return `$${roundedAmount.toLocaleString('es-MX', {
        //minimumFractionDigits: 2,
        maximumFractionDigits: 0
    })}`;
};
export default Dashboard;

